/**
 * Single owner of the posthog-js instance.
 *
 * posthog-js is ~250 KB raw. gatsby-browser.js has always loaded it with a
 * dynamic `import()` inside `requestIdleCallback` to keep it off the boot
 * path — but three calculator controllers ALSO imported it statically, and
 * webpack's `lib` cacheGroup (`chunks: "all"`) therefore hoisted its vendor
 * chunk into the *initial* chunk group of the page templates. The result: the
 * chunk was downloaded, preloaded with high priority and executed on every
 * page load anyway, dynamic import or not
 * (`ai-cms/reports/pagespeed-plan-2026-09-14.md` §1.3.3).
 *
 * Everything now goes through this module: one dynamic import, one `init`, one
 * promise. Consumers that just want to record something use the
 * fire-and-forget helpers below and never touch the library type, so no static
 * import of posthog-js survives anywhere in the bundle.
 */

const POSTHOG_TOKEN = "phc_cqikHGbaVk8UlXsJgS0aQes1NCp8hmnHNrMZsHj6RyD";
const POSTHOG_API_HOST = "https://eu.i.posthog.com";

let posthogPromise: Promise<any> | null = null;

/**
 * Load and initialise posthog-js once per page load. Returns the same promise
 * on every call, so whoever gets there first (the idle task in
 * gatsby-browser.js, or a calculator event if the user is fast) pays for the
 * import and everyone else waits on it.
 *
 * `onReady` runs inside posthog's own `loaded` callback — that is where the
 * `$pageview` capture and the web-vitals reporter live.
 */
export const initPosthog = (onReady?: (posthog: any) => void): Promise<any> => {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (!posthogPromise) {
    posthogPromise = import("posthog-js")
      .then(({ default: posthog }) => {
        posthog.init(POSTHOG_TOKEN, {
          api_host: POSTHOG_API_HOST,
          autocapture: false,
          capture_pageview: false,
          loaded: () => {
            if (onReady) onReady(posthog);
          },
        });
        return posthog;
      })
      .catch(() => null);
  }
  return posthogPromise;
};

/**
 * The initialised instance, or `null` if posthog never loaded (blocked,
 * offline, very old browser). Callers must not assume it resolves to a usable
 * object — use the helpers below unless you really need the library.
 */
export const getPosthog = (): Promise<any> => initPosthog();

/** Fire-and-forget `posthog.capture`. Never throws, never blocks the caller. */
export const posthogCapture = (
  event: string,
  properties?: Record<string, unknown>
): void => {
  getPosthog()
    .then((posthog) => posthog && posthog.capture(event, properties))
    .catch(() => undefined);
};

/** Fire-and-forget `posthog.identify`. */
export const posthogIdentify = (
  distinctId: string,
  set?: Record<string, unknown>,
  setOnce?: Record<string, unknown>
): void => {
  getPosthog()
    .then((posthog) => posthog && posthog.identify(distinctId, set, setOnce))
    .catch(() => undefined);
};
